/**
 * Dropdown Validation Utilities
 */
import { WILDLIFE_DROPDOWN_OPTIONS } from '../constants/wildlifeDropdowns';

/**
 * Validates if a selected value is in the list of valid options
 * 
 * @param {String} value - The selected value
 * @param {Array} options - Array of valid options
 * @returns {Object} - Validation result with isValid and message properties
 */
export const validateDropdownSelection = (value, options) => {
  // If empty, it's valid (most dropdowns are optional)
  if (!value || value.trim() === '') {
    return {
      isValid: true,
      message: ''
    };
  }
  
  // Check if the value is in the list of valid options
  const isValid = options.includes(value);
  
  return {
    isValid,
    message: isValid ? '' : 'Selected value is not a valid option'
  };
};

/**
 * Validates species selection
 * 
 * @param {String} species - The selected species
 * @returns {Object} - Validation result with isValid and message properties
 */
export const validateSpecies = (species) => {
  return validateDropdownSelection(species, WILDLIFE_DROPDOWN_OPTIONS.SPECIES_OPTIONS);
};

/**
 * Validates UTM Zone selection
 *
 * @param {String} utmZone - The selected UTM Zone
 * @returns {Object} - Validation result with isValid and message properties
 */
export const validateUTMZone = (utmZone) => {
  return validateDropdownSelection(utmZone, WILDLIFE_DROPDOWN_OPTIONS.UTM_ZONE_OPTIONS);
};

/**
 * Validates Animal Distance selection
 *
 * @param {String} animalDistance - The selected Animal Distance
 * @returns {Object} - Validation result with isValid and message properties
 */
export const validateAnimalDistance = (animalDistance) => {
  return validateDropdownSelection(animalDistance, WILDLIFE_DROPDOWN_OPTIONS.ANIMAL_DISTANCE_OPTIONS);
};

/**
 * Validates Aspect of Animal selection
 *
 * @param {String} aspectOfAnimal - The selected Aspect of Animal
 * @returns {Object} - Validation result with isValid and message properties
 */
export const validateAspectOfAnimal = (aspectOfAnimal) => {
  return validateDropdownSelection(aspectOfAnimal, WILDLIFE_DROPDOWN_OPTIONS.ASPECT_OF_ANIMAL_OPTIONS);
};

/**
 * Validates Elevation selection
 *
 * @param {String} elevation - The selected Elevation
 * @returns {Object} - Validation result with isValid and message properties
 */
export const validateElevation = (elevation) => {
  // If elevation is a number (from database), convert it to string for validation
  if (typeof elevation === 'number') {
    const elevationMap = {
      3: 'Alpine',
      2: 'Treeline',
      1: 'Below Treeline'
    };
    elevation = elevationMap[elevation] || elevation;
  }
  return validateDropdownSelection(elevation, WILDLIFE_DROPDOWN_OPTIONS.ELEVATION_OPTIONS);
};

/**
 * Validates Animal Reaction selection
 * 
 * @param {String} animalReaction - The selected Animal Reaction
 * @returns {Object} - Validation result with isValid and message properties
 */
export const validateAnimalReaction = (animalReaction) => {
  return validateDropdownSelection(animalReaction, WILDLIFE_DROPDOWN_OPTIONS.ANIMAL_REACTION_OPTIONS);
};

/**
 * Validates Action selection (for both Immediate and End of Day)
 *
 * @param {String} action - The selected Action
 * @returns {Object} - Validation result with isValid and message properties
 */
export const validateAction = (action) => {
  return validateDropdownSelection(action, WILDLIFE_DROPDOWN_OPTIONS.ACTION_OPTIONS);
};

/**
 * Validates Inventory Method selection
 *
 * @param {String} inventoryMethod - The selected Inventory Method
 * @returns {Object} - Validation result with isValid and message properties
 */
export const validateInventoryMethod = (inventoryMethod) => {
  return validateDropdownSelection(inventoryMethod, WILDLIFE_DROPDOWN_OPTIONS.INVENTORY_METHOD_OPTIONS);
};

/**
 * Validates Machine Type selection
 *
 * @param {String} machineType - The selected Machine Type
 * @returns {Object} - Validation result with isValid and message properties
 */
export const validateMachineType = (machineType) => {
  return validateDropdownSelection(machineType, WILDLIFE_DROPDOWN_OPTIONS.MACHINE_TYPE_OPTIONS);
};

export default {
  validateDropdownSelection,
  validateSpecies,
  validateUTMZone,
  validateAnimalDistance,
  validateAspectOfAnimal,
  validateElevation,
  validateAnimalReaction,
  validateAction,
  validateInventoryMethod,
  validateMachineType
};